import * as React from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { Search, X } from "lucide-react";

export default function BasicSelect({
  value,
  label,
  handleChange,
  handleBlur,
  disabledValues = [],
  items = [],
  name,
  MenuProps,
  canSearch = false,
  ...props
}) {
  const [searchText, setSearchText] = React.useState("");

  const filteredItems = React.useMemo(() => {
    if (!canSearch || !searchText.trim()) return items;
    return items.filter((item) =>
      item.label?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [items, searchText, canSearch]);

  const selectedLabel = React.useMemo(
    () => items.find((i) => i.value === value)?.label || "",
    [items, value],
  );

  // ✅ AFTER
  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Use setTimeout to let MUI finish its internal click handling first
    // then fire the clear — otherwise MUI re-selects the current value
    setTimeout(() => {
      handleChange({ target: { name, value: "" } });
    }, 0);
  };

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl fullWidth {...props}>
        <InputLabel id={`${name}-label`}>{label}</InputLabel>
        <Select
          labelId={`${name}-label`}
          id={`${name}-select`}
          name={name}
          value={value}
          label={label}
          displayEmpty
          onChange={handleChange}
          onBlur={handleBlur}
          onClose={() => setSearchText("")}
          MenuProps={{
            ...MenuProps,
            autoFocus: false,
            disablePortal: true, // ← ADD THIS
            PaperProps: {
              style: { maxHeight: 300 },
              ...MenuProps?.PaperProps,
            },
          }}
          // Show selected value as grey pill with X inside the input
          renderValue={() =>
            selectedLabel ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#f3f4f6",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.3,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  maxWidth: "100%",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 180,
                  }}
                >
                  {selectedLabel}
                </span>
                <Box
                  component="span"
                  onClick={handleClear}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "#6b7280",
                    ml: 0.3,
                    flexShrink: 0,
                    "&:hover": { color: "#111827" },
                  }}
                >
                  <X size={12} strokeWidth={2.5} />
                </Box>
              </Box>
            ) : (
              ""
            )
          }
        >
          {canSearch && (
            <Box
              sx={{
                px: 1,
                pb: 1,
                pt: 0.5,
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
              // Stop ALL events from bubbling up to MUI Select
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search..."
                value={searchText}
                autoFocus={false}
                onChange={(e) => setSearchText(e.target.value)}
                inputProps={{
                  // Stop MUI Select from intercepting input-level events
                  onMouseDown: (e) => e.stopPropagation(),
                  onClick: (e) => e.stopPropagation(),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={14} color="#9ca3af" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { fontSize: 13 },
                }}
              />
            </Box>
          )}

          {filteredItems.length === 0 ? (
            <MenuItem disabled sx={{ fontSize: 13, color: "#9ca3af" }}>
              No options found
            </MenuItem>
          ) : (
            filteredItems.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                disabled={disabledValues.includes(item.value)}
                sx={{
                  fontSize: 13,
                  backgroundColor:
                    value === item.value ? "#f0f4ff !important" : "transparent",
                  fontWeight: value === item.value ? 600 : 400,
                }}
              >
                {item.label}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
}
